/**
 * Unde ateriza userul dupa login.
 *
 * Pana la 2026-08-19, raspunsul era hardcodat: `/dashboard`. Masurat pe un cont
 * real, portofoliul propriu incepea la 2.651 px pe o pagina de 5.531 px, sub 10
 * blocuri de analiza agregata — deci "am ajuns pe dashboard" nu insemna "vad
 * datele brandului meu".
 *
 * Benchmark (10 produse de AI visibility documentate din surse primare —
 * Profound, Peec, Scrunch, AthenaHQ, Otterly, Ahrefs, Semrush...): NICIUNUL nu
 * aterizeaza pe o lista de branduri. Toate aterizeaza pe dashboard-ul unui
 * singur brand sau pe un punct de intrare de tip cautare.
 *
 * Ierarhia de mai jos urmeaza tiparul convergent documentat la Linear, Datadog
 * si Amplitude: preferinta explicita a userului bate un default derivat din
 * starea reala a contului. Datadog o formuleaza cel mai clar — daca folosesti
 * APM aterizezi pe APM, daca nu, pe lista de dashboard-uri.
 */

/** Cat timp mai e valabila o intentie de deep link. Peste asta, un 401 abandonat
 *  saptamana trecuta ar teleporta login-ul de azi undeva fara legatura. */
export const RETURN_TO_TTL_MS = 30 * 60 * 1000; // 30 min

export const RETURN_TO_KEY = 'brandvis_return_to';

/**
 * Citeste si consuma intentia de deep link, daca e inca valabila.
 * Sterge intotdeauna cheia — o intentie expirata nu are voie sa supravietuiasca
 * pana la urmatorul login.
 *
 * @returns {string|null} path intern, sau null
 */
export function consumeReturnTo() {
  let raw = null;
  try {
    raw = localStorage.getItem(RETURN_TO_KEY);
    localStorage.removeItem(RETURN_TO_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let path = raw;
  let savedAt = null;

  // Formatul nou e JSON cu timestamp; il acceptam si pe cel vechi (string simplu)
  // ca sa nu pierdem intentia userilor care au deja cheia scrisa in browser.
  if (raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      path = parsed?.path || null;
      savedAt = parsed?.savedAt || null;
    } catch {
      return null;
    }
  }

  if (!isSafeInternalPath(path)) return null;
  if (savedAt && Date.now() - savedAt > RETURN_TO_TTL_MS) return null;
  return path;
}

/** Salveaza intentia de deep link, cu timestamp. */
export function saveReturnTo(path) {
  if (!isSafeInternalPath(path) || path === '/login') return;
  try {
    localStorage.setItem(RETURN_TO_KEY, JSON.stringify({ path, savedAt: Date.now() }));
  } catch {}
}

/**
 * Acceptam DOAR path-uri interne. Fara asta, o valoare din localStorage ajunge
 * direct in `navigate()`: `//evil.com` e tratat de browser ca protocol-relative
 * si duce in afara aplicatiei.
 */
export function isSafeInternalPath(path) {
  return (
    typeof path === 'string' &&
    path.length > 0 &&
    path.length < 512 &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    !path.startsWith('/\\')
  );
}

/**
 * Decide destinatia post-login.
 *
 * @param {object}   user    profilul intors de login (primary_brand_id, last_brand_id)
 * @param {Array}    brands  brandurile userului (deja filtrate de cele seed-uite)
 * @returns {string} path intern
 */
export function resolvePostLoginPath(user, brands) {
  // 1. Intentia explicita a userului (deep link intrerupt de un 401).
  const returnTo = consumeReturnTo();
  if (returnTo) return returnTo;

  const owned = ownedBrands(brands);
  const exists = (id) => !!id && owned.some((b) => b.id === id);

  // 2. Brandul ales explicit ca destinatie.
  if (exists(user?.primary_brand_id)) return `/brand/${user.primary_brand_id}`;

  // 3. Ultimul brand vizitat.
  if (exists(user?.last_brand_id)) return `/brand/${user.last_brand_id}`;

  // 4. Derivat din starea contului.
  if (owned.length === 1) return `/brand/${owned[0].id}`;

  // 0 branduri proprii → portofoliul are un empty state util (CTA de adaugare),
  // deci ramane dashboard-ul. N branduri → userul chiar are ce alege.
  return '/dashboard';
}

/**
 * Brandurile pe care userul le-a adaugat el. Cele seed-uite automat
 * (backend/services/banking_seed.py) nu conteaza pentru decizia de aterizare:
 * altfel orice cont de banking ar avea "33 de branduri" si n-ar nimeri niciodata
 * regula "1 brand → deschide-l".
 */
export function ownedBrands(brands) {
  if (!Array.isArray(brands)) return [];
  return brands.filter((b) => b && !b.is_seeded && !b.isPublic);
}

export default resolvePostLoginPath;
